import { emailSchema } from '@fos/shared/contracts/common';
import {
  type AssignableRole,
  INVITATION_TTL_DAYS,
  type InviteMemberInput,
  templatePermissions,
} from '@fos/shared/contracts/team';
import { DATA_SCOPES, type DataScope, PERMISSION_TEMPLATES, type PermissionTemplate } from '@fos/shared/enums';
import {
  DATA_SCOPE_LABELS,
  PERMISSION_TEMPLATE_LABELS,
  TEAM_AI_LIMIT_NOTE,
  TEAM_AI_LIMIT_PARTNER_NOTE,
  TEAM_DATA_SCOPE_DESCRIPTIONS,
  templateSummary,
} from '@fos/shared/labels';
import { Lock, Send } from 'lucide-react';
import { type FormEvent, useEffect, useMemo, useRef, useState } from 'react';
import { appErrorCode, errorMessage, validationIssues } from '../../../lib/api';
import { trpc } from '../../../lib/trpc';
import { useFocusFirstInvalid } from '../../../lib/useFocusInvalid';
import { FormMessage } from '../../../ui/Blocks';
import { Button } from '../../../ui/Button';
import { Dialog } from '../../../ui/Dialog';
import { Field, Label } from '../../../ui/Field';
import { Input } from '../../../ui/Input';
import { Select } from '../../../ui/Select';
import { toast } from '../../../ui/Toast';
import { FULLSCREEN_ON_PHONE, TOUCH_TEXT } from '../../mobile';
import { ChoiceCards } from './components/ChoiceCards';
import { ClientColorDot, ResourcePicker } from './components/ResourcePicker';
import { aiLimitInputAfterTemplate, parseAiLimit, ROLE_CHOICES } from './team-ui';

type TemplateChoice = PermissionTemplate | 'none';

/** Szablon, który naprawdę obowiązuje (tylko rola Użytkownik). */
function effectiveTemplate(role: AssignableRole, template: TemplateChoice): PermissionTemplate | null {
  return role === 'user' && template !== 'none' ? template : null;
}
type FieldErrors = Partial<Record<'email' | 'aiLimit', string>>;

const TEMPLATE_OPTIONS = [
  ...PERMISSION_TEMPLATES.map((t) => ({ value: t as TemplateChoice, label: PERMISSION_TEMPLATE_LABELS[t] })),
  { value: 'none' as TemplateChoice, label: 'Bez szablonu (moduły ustawisz później)' },
];

const SCOPE_OPTIONS = DATA_SCOPES.map((s) => ({ value: s, label: DATA_SCOPE_LABELS[s] }));

const INITIAL = {
  email: '',
  role: 'user' as AssignableRole,
  template: 'developer' as TemplateChoice,
  dataScope: 'assigned' as DataScope,
  clientIds: [] as string[],
  aiLimit: '',
};

/**
 * Modal „Zaproś osobę” (Figma 3035:4324): email, rola (karty), szablon uprawnień z „Dostosuj”,
 * udostępnieni klienci, limit AI. Wysyłka wymaga potwierdzenia hasłem (step-up obsługuje link tRPC).
 */
export function InviteDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (open: boolean) => void }) {
  const utils = trpc.useUtils();
  const invite = trpc.team.invite.useMutation();
  const options = trpc.team.grantOptions.useQuery(undefined, { enabled: open, staleTime: 60_000 });
  const [form, setForm] = useState(INITIAL);
  const [customize, setCustomize] = useState(false);
  const [errors, setErrors] = useState<FieldErrors>({});
  const [formError, setFormError] = useState<string | null>(null);
  const formRef = useRef<HTMLFormElement>(null);
  useFocusFirstInvalid(formRef, errors);

  useEffect(() => {
    if (open) {
      setForm(INITIAL);
      setCustomize(false);
      setErrors({});
      setFormError(null);
    }
  }, [open]);

  const set = <K extends keyof typeof INITIAL>(key: K, value: (typeof INITIAL)[K]) =>
    setForm((f) => ({ ...f, [key]: value }));

  // szablon Partner wpisuje limit AI 0 zł (AI we własnym workspace'ie); odejście od niego czyści limit z szablonu
  const chooseTemplate = (template: TemplateChoice) =>
    setForm((f) => ({
      ...f,
      template,
      dataScope: template === 'none' ? 'selected' : templatePermissions(template).dataScope,
      aiLimit: aiLimitInputAfterTemplate(
        effectiveTemplate(f.role, f.template),
        effectiveTemplate(f.role, template),
        f.aiLimit,
      ),
    }));

  const chooseRole = (role: AssignableRole) =>
    setForm((f) => ({
      ...f,
      role,
      aiLimit: aiLimitInputAfterTemplate(
        effectiveTemplate(f.role, f.template),
        effectiveTemplate(role, f.template),
        f.aiLimit,
      ),
    }));

  const clients = options.data?.clients ?? [];
  const picked = useMemo(
    () =>
      form.clientIds
        .map((id) => clients.find((c) => c.id === id))
        .filter((c): c is (typeof clients)[number] => !!c)
        .map((c) => ({ id: c.id, label: c.name, color: c.color })),
    [form.clientIds, clients],
  );
  const clientOptions = clients
    .filter((c) => !form.clientIds.includes(c.id))
    .map((c) => ({ value: c.id, label: c.name, leading: <ClientColorDot color={c.color} /> }));

  const isUser = form.role === 'user';

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setFormError(null);
    const next: FieldErrors = {};
    const email = emailSchema.safeParse(form.email);
    if (!email.success) next.email = form.email.trim() ? 'Nieprawidłowy adres email' : 'Podaj adres email';
    const limit = parseAiLimit(form.aiLimit);
    if (limit.error) next.aiLimit = limit.error;
    setErrors(next);
    if (Object.keys(next).length > 0 || !email.success) return;

    const input: InviteMemberInput = {
      email: email.data,
      role: form.role,
      template: isUser && form.template !== 'none' ? form.template : null,
      ...(isUser ? { dataScope: form.dataScope, clientIds: form.clientIds } : {}),
      aiMonthlyLimitPln: limit.value,
    };
    try {
      await invite.mutateAsync(input);
      toast.success('Zaproszenie wysłane', {
        description: `${email.data} dostanie link ważny ${INVITATION_TTL_DAYS} dni.`,
      });
      void utils.team.overview.invalidate();
      onOpenChange(false);
    } catch (err) {
      const code = appErrorCode(err);
      if (code === 'STEP_UP_REQUIRED') return; // anulowane potwierdzenie hasłem
      const emailIssue = validationIssues(err).find((i) => i.path === 'email');
      if (emailIssue) {
        setErrors({ email: emailIssue.message });
        return;
      }
      setFormError(errorMessage(err));
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      width="l"
      title="Zaproś osobę"
      className={FULLSCREEN_ON_PHONE}
      description={`Dostanie maila z linkiem. Zaproszenie ważne ${INVITATION_TTL_DAYS} dni.`}
    >
      <form ref={formRef} onSubmit={submit} noValidate className="flex flex-col gap-5" aria-label="Zaproś osobę">
        <Field label="E-mail" error={errors.email}>
          <Input
            type="email"
            autoComplete="off"
            autoFocus
            placeholder="imie@firma.pl"
            value={form.email}
            onChange={(e) => set('email', e.target.value)}
          />
        </Field>

        <div className="flex flex-col gap-1.5">
          <Label>Rola</Label>
          <ChoiceCards aria-label="Rola" columns value={form.role} onValueChange={chooseRole} options={ROLE_CHOICES} />
        </div>

        {isUser && (
          <div className="rounded-lg bg-surface px-4 py-3">
            <div className="flex items-start gap-3">
              <Lock size={16} strokeWidth={1.75} aria-hidden className="mt-0.5 shrink-0 text-fg-muted" />
              <div className="min-w-0 flex-1">
                <p className="text-body-s font-medium text-fg">
                  {form.template === 'none'
                    ? 'Bez szablonu uprawnień'
                    : `Szablon uprawnień: ${PERMISSION_TEMPLATE_LABELS[form.template]}`}
                </p>
                <p className="mt-0.5 text-caption text-fg-muted">
                  {form.template === 'none'
                    ? 'Moduły i przełączniki ustawisz po dołączeniu osoby.'
                    : templateSummary(form.template)}
                  {' · '}
                  {DATA_SCOPE_LABELS[form.dataScope].toLowerCase()}
                </p>
              </div>
              {!customize && (
                <button
                  type="button"
                  onClick={() => setCustomize(true)}
                  className={`shrink-0 rounded-xs text-body-s font-medium text-accent hover:underline focus-visible:outline-2 focus-visible:outline-accent ${TOUCH_TEXT}`}
                >
                  Dostosuj
                </button>
              )}
            </div>
            {customize && (
              <div className="mt-3 grid gap-3 md:grid-cols-2">
                <Field label="Szablon">
                  <Select value={form.template} onValueChange={chooseTemplate} options={TEMPLATE_OPTIONS} />
                </Field>
                <Field label="Zakres danych" hint={TEAM_DATA_SCOPE_DESCRIPTIONS[form.dataScope]}>
                  <Select value={form.dataScope} onValueChange={(v) => set('dataScope', v)} options={SCOPE_OPTIONS} />
                </Field>
                <p className="text-caption text-fg-muted md:col-span-2">
                  Szablon to punkt startowy. Pojedyncze moduły i przełączniki zmienisz w uprawnieniach osoby.
                </p>
              </div>
            )}
          </div>
        )}

        {isUser && form.dataScope !== 'all' && (
          <div className="flex flex-col gap-1.5">
            <Label>Udostępnij klientów (opcjonalnie)</Label>
            <ResourcePicker
              aria-label="Udostępnieni klienci"
              items={picked}
              options={clientOptions}
              onAdd={(id) => set('clientIds', [...form.clientIds, id])}
              onRemove={(id) =>
                set(
                  'clientIds',
                  form.clientIds.filter((c) => c !== id),
                )
              }
              placeholder={options.isPending ? 'Wczytywanie klientów…' : 'Dodaj klienta…'}
              emptyText={options.isError ? 'Nie udało się wczytać klientów' : 'Brak klientów'}
            />
          </div>
        )}

        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <Label htmlFor="invite-ai-limit">Limit AI miesięcznie</Label>
            <p className="mt-0.5 text-caption text-fg-muted">
              {effectiveTemplate(form.role, form.template) === 'partner'
                ? TEAM_AI_LIMIT_PARTNER_NOTE
                : TEAM_AI_LIMIT_NOTE}
            </p>
            {errors.aiLimit && (
              <p role="alert" className="mt-1 text-caption text-danger">
                {errors.aiLimit}
              </p>
            )}
          </div>
          <div className="relative w-24 shrink-0">
            <Input
              id="invite-ai-limit"
              mono
              inputMode="decimal"
              placeholder="—"
              aria-invalid={errors.aiLimit ? true : undefined}
              value={form.aiLimit}
              onChange={(e) => set('aiLimit', e.target.value)}
              className="pr-8 text-right"
            />
            <span
              aria-hidden
              className="pointer-events-none absolute top-1/2 right-3 -translate-y-1/2 text-caption text-fg-muted"
            >
              zł
            </span>
          </div>
        </div>

        {formError && <FormMessage>{formError}</FormMessage>}

        <div className="flex flex-wrap items-center justify-end gap-2 pt-1">
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Anuluj
          </Button>
          <Button type="submit" variant="primary" icon={Send} loading={invite.isPending}>
            Wyślij zaproszenie
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
